import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
  escortForms: [],
  currentEscortForm: null,
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch all Escort Forms
export const fetchEscortForms = createAsyncThunk(
  'escortForms/fetchEscortForms',
  async (patientId, { rejectWithValue }) => {
    try {
      const url = patientId ? `/escort-form-home-visitation/${patientId}` : '/escort-form-home-visitation';
      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch Escort forms'
      );
    }
  }
);

// Fetch a single Escort Form
export const fetchEscortForm = createAsyncThunk(
  'escortForms/fetchEscortForm',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/escort-form-home-visitation/show/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch Escort form'
      );
    }
  }
);

// Create a new Escort Form
export const createEscortForm = createAsyncThunk(
  'escortForms/createEscortForm',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post('/escort-form-home-visitation', formData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to create Escort form'
      );
    }
  }
);

// Update an existing Escort Form
export const updateEscortForm = createAsyncThunk(
  'escortForms/updateEscortForm',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/escort-form-home-visitation/${id}`, formData);
      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to update Escort form'
      );
    }
  }
);

// Delete an Escort Form
export const deleteEscortForm = createAsyncThunk(
  'escortForms/deleteEscortForm',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/escort-form-home-visitation/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to delete Escort form'
      );
    }
  }
);

// ==================== Slice ====================
const escortFormSlice = createSlice({
  name: 'escortForms',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentEscortForm: (state, action) => {
      state.currentEscortForm = action.payload;
    },
    clearCurrentEscortForm: (state) => {
      state.currentEscortForm = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH ALL
      .addCase(fetchEscortForms.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEscortForms.fulfilled, (state, action) => {
        state.isLoading = false;
        state.escortForms = action.payload;
      })
      .addCase(fetchEscortForms.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH SINGLE
      .addCase(fetchEscortForm.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEscortForm.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentEscortForm = action.payload;
      })
      .addCase(fetchEscortForm.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createEscortForm.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createEscortForm.fulfilled, (state, action) => {
        state.isLoading = false;
        state.escortForms.unshift(action.payload);
      })
      .addCase(createEscortForm.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateEscortForm.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateEscortForm.fulfilled, (state, action) => {
        state.isLoading = false;
        const updated = action.payload;
        const index = state.escortForms.findIndex((d) => d.id === updated.id);
        if (index !== -1) {
          state.escortForms[index] = updated;
        }
        if (state.currentEscortForm?.id === updated.id) {
          state.currentEscortForm = updated;
        }
      })
      .addCase(updateEscortForm.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteEscortForm.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteEscortForm.fulfilled, (state, action) => {
        state.isLoading = false;
        state.escortForms = state.escortForms.filter(
          (d) => d.id !== action.payload
        );
        if (state.currentEscortForm?.id === action.payload) {
          state.currentEscortForm = null;
        }
      })
      .addCase(deleteEscortForm.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  setCurrentEscortForm,
  clearCurrentEscortForm,
} = escortFormSlice.actions;

export default escortFormSlice.reducer;
